// react
import React, { useRef, forwardRef, useMemo, useState, useCallback, useEffect } from 'react';
import { Empty, Bottom } from '@shakkerai/pro-components';
import { LoadingOverlay, ScrollArea } from '@mantine/core';
import styles from './index.modules.scss';
import { postHistoryHome } from '@shakkerai/services';
import { translate } from '@shakkerai/util-i18n';
import { useGenHistoryVisible } from './store/base';
import { convert2CompressedUrl } from '@shakkerai/util-image';

function getTodayDate() {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}
export const HistoryHomeList = forwardRef((props, ref) => {
  const { scrollContainerRef, noDateStr, onSelected, sortValueRef } = props;
  const [dataList, setDataList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [startDate, setStartDate] = useState(null);
  const [noMore, setNoMore] = useState(false);
  const { genHistoryVisible } = useGenHistoryVisible();
  const scrollWrapperRef = useRef(null);

  const fetchData = useCallback(
    (pageNo) => {
      setLoading(true);
      postHistoryHome({
        createDate: startDate?.day || '',
        dataReload: 'event_historyList_home',
        pageNo: pageNo,
        pageSize: 1000,
        sort: sortValueRef?.current,
      })
        .then((res) => {
          if (res?.code === 0 && res?.data) {
            const list = res.data.list || [];
            const allData = [...dataList, ...list];
            setDataList(allData);
            if (res.data.total >= res.data.pageSize) {
              setNoMore(false);
            } else {
              setNoMore(true);
            }
            const start = sortValueRef?.current === -1 ? list.length - 1 : 0;
            setStartDate(list[start]);
          }
          setLoading(false);
        })
        .catch((error) => {
          console.error('fetchData error:', error);
          setLoading(false);
        });
    },
    [startDate, sortValueRef, dataList],
  );

  const isEmpty = useMemo(() => {
    return !dataList?.length;
  }, [dataList]);

  useEffect(() => {
    if (genHistoryVisible) {
      fetchData(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [genHistoryVisible]);

  const handleLoadMore = useCallback(
    (e) => {
      e.stopPropagation();
      if (noMore || loading) {
        return;
      }
      const newNo = pageNumber + 1;
      setPageNumber(newNo);
      fetchData(newNo);
    },
    [pageNumber, fetchData, noMore, loading],
  );

  // 加一个标志，防止后退的时候重复进入这个逻辑
  const hasReloadDataRef = useRef(false);
  useEffect(() => {
    if (!genHistoryVisible) {
      hasReloadDataRef.current = false;
    }
  }, [genHistoryVisible]);
  useEffect(() => {
    if (!hasReloadDataRef.current) {
      const today = getTodayDate();
      const hasDay = dataList?.find((item) => item.day === today);
      if (hasDay && hasDay.day) {
        onSelected?.(hasDay);
      }
      hasReloadDataRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataList]);

  return (
    <div ref={ref} className="relative h-full">
      <div className={`${styles.scrollC}`}>
        {loading && pageNumber === 1 ? (
          <LoadingOverlay visible={loading} zIndex={1000} overlayProps={{ radius: 'sm', blur: 2 }} />
        ) : !isEmpty ? (
          <ScrollArea viewportRef={scrollContainerRef} scrollbarSize={4}>
            <div ref={scrollWrapperRef} className={`flex flex-wrap`} data-his-c>
              {dataList?.map((item, index) => {
                const key = item.imageUrl + index;
                return (
                  <div
                    key={key}
                    onClick={() => onSelected?.(item)}
                    data-role="gen-history-trigger"
                    className={`w-[256px] cursor-pointer h-[256px] overflow-hidden ml-[16px] mb-[16px] relative rounded-[4px]`}
                  >
                    <img
                      className="w-full h-full object-cover hover:scale-[1.1] transition duration-300 ease-in-out"
                      src={convert2CompressedUrl(item.imageUrl)}
                      loading="lazy"
                      alt={item.day}
                    />
                    <div
                      className={`w-full absolute bottom-0 flex justify-between p-[12px] text-[14px] font-semibold text-[#fff] ${styles.itemBottomWrap}`}
                    >
                      <span>{item.dayShow}</span>
                      <span>
                        <i className="text-primary not-italic mr-[4px]">{item.count}</i>{' '}
                        {translate('history_bydateimg')}
                      </span>
                    </div>
                  </div>
                );
              })}
              <div className="text-center w-full">
                <Bottom
                  loadingMore={loading}
                  onClick={handleLoadMore}
                  noMore={noMore}
                  className="pl-[170px] pr-[170px] pt-[17px] pb-[34px]"
                ></Bottom>
              </div>
            </div>
          </ScrollArea>
        ) : (
          <div className={styles.emptyC}>
            <Empty content={noDateStr} />
          </div>
        )}
      </div>
    </div>
  );
});

export default HistoryHomeList;
